/**
 * Share helpers — generate a URL that, when posted on social media / messaging
 * apps, produces a rich preview with image + title + price + description.
 *
 * How it works: the URL points to `/api/og/{kind}/{id}` on the same origin.
 * Social-media crawlers (FB / WhatsApp / Telegram / Twitter / iMessage) follow
 * it, parse the OG meta tags returned by the backend, and render a preview.
 * Real browsers get redirected to the React route in <100ms via meta refresh + JS.
 */

const PUBLIC_ORIGIN = (typeof window !== 'undefined' ? window.location.origin : '') || '';

export const buildListingShareUrl = (slugOrId) => {
  if (!slugOrId) return PUBLIC_ORIGIN || '';
  return `${PUBLIC_ORIGIN}/api/og/listing/${encodeURIComponent(slugOrId)}`;
};

export const buildShopShareUrl = (userId) => {
  if (!userId) return PUBLIC_ORIGIN || '';
  return `${PUBLIC_ORIGIN}/api/og/shop/${encodeURIComponent(userId)}`;
};

export const buildBlogShareUrl = (slug) => {
  if (!slug) return PUBLIC_ORIGIN || '';
  return `${PUBLIC_ORIGIN}/api/og/blog/${encodeURIComponent(slug)}`;
};

export const buildInviteShareUrl = (code) => {
  if (!code) return PUBLIC_ORIGIN || '';
  return `${PUBLIC_ORIGIN}/api/og/invite/${encodeURIComponent(code)}`;
};

/**
 * Share via the native picker if available, otherwise copy to clipboard.
 * Resolves with an object describing what happened so the caller can show feedback.
 */
export const shareOrCopy = async ({ url, title = '', text = '' }) => {
  if (!url) return { ok: false, mode: 'noop' };
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ url, title, text });
      return { ok: true, mode: 'native' };
    } catch (e) {
      // User dismissed — fall through to clipboard so they still get the URL.
      if (e && e.name === 'AbortError') return { ok: false, mode: 'aborted' };
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return { ok: true, mode: 'clipboard' };
  } catch (e) {
    return { ok: false, mode: 'failed', error: e };
  }
};
